/**
 * What the easing dialogs show about the segment they edit (W2-05): its property, start and end values in friendly
 * units, its length, and its span in chart seconds (the live preview loops it).
 */

import { buildTempoMap } from '../../core/doc/container';
import { isUiMover, kindOfLine } from '../../core/model/lineKind';
import { Units } from '../../core/model/units';
import type { ChartDoc, DocIndexes, KeyId, LineId, LineProp } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { PreviewSpan } from './livePreview';

export interface SegmentPreviewInfo {
  readonly key: KeyId;
  readonly line: LineId | null;
  readonly prop: LineProp | null;
  /** Numeric start / end values in friendly units (null for colours, texts and vectors). */
  readonly from: number | null;
  readonly to: number | null;
  readonly start: Beat;
  readonly end: Beat;
  /** Length in beats (f64, for display). */
  readonly beats: number;
  readonly span: PreviewSpan;
}

export function segmentPreview(doc: ChartDoc, idx: DocIndexes, key: KeyId): SegmentPreviewInfo | null {
  const k = doc.keys.get(key);
  const nextId = idx.nextKey(key);
  const next = nextId === undefined ? undefined : doc.keys.get(nextId);
  if (k === undefined || next === undefined) return null;
  const track = doc.tracks.get(k.track);
  const owner = track?.owner;
  const line = owner?.kind === 'line' ? owner.line : null;
  const prop = owner?.kind === 'line' ? owner.prop : null;
  const rec = line === null ? undefined : doc.lines.get(line);
  const friendly = (v: unknown): number | null => {
    if (typeof v !== 'number') return null;
    if (prop === null || rec === undefined) return v;
    return Units.toFriendly(prop, v, { kind: kindOfLine(doc, idx, rec), uiMover: isUiMover(rec) });
  };
  const tempo = buildTempoMap(doc, idx);
  return {
    key,
    line,
    prop,
    from: friendly(k.vOut),
    to: friendly(next.vIn),
    start: k.t,
    end: next.t,
    beats: Beats.toF64(next.t) - Beats.toF64(k.t),
    span: { t0: tempo.secAt(k.t), t1: tempo.secAt(next.t) },
  };
}

/** A friendly value as text (at most 2 decimals). */
export function formatValue(v: number): string {
  const r = Math.round(v * 100) / 100;
  return String(r === 0 ? 0 : r);
}
